import { ref, computed } from "vue"
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  Timestamp,
  FieldValue,
  getFirestore, // Firestore 인스턴스를 가져오기 위해 추가
} from "firebase/firestore"
import { firebaseApp } from "../firebase" // chatbot-widget의 Firebase 앱 인스턴스 경로 (가정)

// 카테고리 타입 정의 (productCategory.store.ts 참고)
export interface ProductCategory {
  id: string
  name: string
  parentId: string | null
  productCount: number
  order: number
  productNo?: string | number | null // store.ts에서는 number였으나, SelectProductModal에서는 string으로도 사용되어 유연하게 변경
  children?: ProductCategory[]
  level?: number
  createdAt: Timestamp // Firestore Timestamp
  updatedAt: Timestamp // Firestore Timestamp
}

export interface TreeOption {
  key: string
  label: string
  totalCount?: number
  children?: TreeOption[]
  level?: number
}

export function useProductCategory() {
  const db = getFirestore(firebaseApp) // Firebase 앱 인스턴스에서 Firestore 가져오기

  const categories = ref<ProductCategory[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)

  const _getCollectionRef = () => {
    return collection(db, "productCategories") // 컬렉션 이름 확인 필요
  }

  const fetchCategories = async () => {
    loading.value = true
    error.value = null
    try {
      const collRef = _getCollectionRef()
      const q = query(collRef, orderBy("order", "asc"), orderBy("createdAt", "asc"))
      const querySnapshot = await getDocs(q)
      const categoriesData: ProductCategory[] = []
      querySnapshot.forEach((docSnap) => {
        categoriesData.push({ id: docSnap.id, ...docSnap.data() } as ProductCategory)
      })
      categories.value = categoriesData
    } catch (err) {
      console.error("[useProductCategory] Fetching categories error:", err)
      error.value = err as Error
      categories.value = []
    } finally {
      loading.value = false
    }
  }

  const createCategory = async (
    name: string,
    parentId: string | null = null,
    productNo?: number,
  ): Promise<ProductCategory | null> => {
    loading.value = true
    error.value = null
    if (!name.trim()) {
      error.value = new Error("Category name is required.")
      loading.value = false
      return null
    }

    try {
      const collRef = _getCollectionRef()
      const siblingCategories = categories.value.filter((cat) => cat.parentId === parentId)
      const maxOrder = siblingCategories.reduce((max, cat) => Math.max(max, cat.order || 0), 0)

      const categoryData: Omit<ProductCategory, "id" | "createdAt" | "updatedAt" | "children" | "level"> = {
        name: name.trim(),
        parentId,
        productCount: 0,
        order: maxOrder + 1,
        ...(productNo !== undefined && { productNo }),
      }

      const docRef = await addDoc(collRef, {
        ...categoryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      // await fetchCategories(); // 생성 후 전체 목록 새로고침 대신, 반환값 사용 또는 개별 추가
      loading.value = false
      // 생성된 카테고리 정보 반환 (id 포함)
      return {
        id: docRef.id,
        ...categoryData,
        createdAt: Timestamp.now(), // 임시값, 실제로는 서버 타임스탬프
        updatedAt: Timestamp.now(), // 임시값
      } as ProductCategory
    } catch (err) {
      console.error("[useProductCategory] Creating category error:", err)
      error.value = err as Error
      loading.value = false
      return null
    }
  }

  const updateCategory = async (id: string, name: string, productNo?: number): Promise<boolean> => {
    loading.value = true
    error.value = null
    if (!name.trim()) {
      error.value = new Error("Category name is required.")
      loading.value = false
      return false
    }
    try {
      const docRef = doc(db, "productCategories", id)
      const updatePayload: { name: string; updatedAt: FieldValue; productNo?: number | FieldValue } = {
        name: name.trim(),
        updatedAt: serverTimestamp(),
      }
      if (productNo !== undefined) {
        updatePayload.productNo = productNo
      } else {
        // productNo를 명시적으로 null 또는 삭제하고 싶을 때. 여기서는 undefined면 업데이트 안 함.
        // Firestore에서 필드를 삭제하려면 deleteField()를 사용합니다.
        // import { deleteField } from 'firebase/firestore';
        // updatePayload.productNo = deleteField();
      }

      await updateDoc(docRef, updatePayload as any) // FieldValue 타입 때문에 any 임시 사용
      // await fetchCategories(); // 업데이트 후 전체 목록 새로고침 대신, 로컬 데이터 업데이트
      const index = categories.value.findIndex((c) => c.id === id)
      if (index !== -1) {
        categories.value[index] = {
          ...categories.value[index],
          name: name.trim(),
          productNo,
          updatedAt: Timestamp.now(),
        }
      }
      loading.value = false
      return true
    } catch (err) {
      console.error("[useProductCategory] Updating category error:", err)
      error.value = err as Error
      loading.value = false
      return false
    }
  }

  const deleteCategory = async (id: string): Promise<boolean> => {
    loading.value = true
    error.value = null
    // 하위 카테고리 존재 여부 등 확인 로직 추가 필요
    try {
      const docRef = doc(db, "productCategories", id)
      await deleteDoc(docRef)
      // await fetchCategories(); // 삭제 후 전체 목록 새로고침 대신, 로컬 데이터에서 제거
      categories.value = categories.value.filter((c) => c.id !== id)
      loading.value = false
      return true
    } catch (err) {
      console.error("[useProductCategory] Deleting category error:", err)
      error.value = err as Error
      loading.value = false
      return false
    }
  }

  // Getters (Computed)
  const categoryTreeData = computed<TreeOption[]>(() => {
    const buildTree = (allCategories: ProductCategory[], parentId: string | null = null, level = 1): TreeOption[] => {
      return allCategories
        .filter((cat) => cat.parentId === parentId)
        .map((cat) => ({
          key: cat.id,
          label: cat.name,
          totalCount: cat.productCount || 0,
          children: buildTree(allCategories, cat.id, level + 1),
          level,
        }))
    }
    return buildTree(categories.value)
  })

  const categoryOptions = computed(() => {
    return categories.value.map((cat) => ({
      label: cat.name,
      value: cat.id,
    }))
  })

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    categoryTreeData,
    categoryOptions,
  }
}

// Helper function (원본 스토어에 있던 것과 유사)
export function findCategoryAndChildren(
  categories: ProductCategory[],
  categoryId: string,
): ProductCategory | undefined {
  const category = categories.find((c) => c.id === categoryId)
  if (!category) return undefined

  const children = categories.filter((c) => c.parentId === categoryId)
  return {
    ...category,
    children: children
      .map((child) => findCategoryAndChildren(categories, child.id))
      .filter((c) => c) as ProductCategory[],
  }
}
